import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, RefreshCw, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Tone = 'muted' | 'warning' | 'danger';

const TONE_CLASS: Record<Tone, string> = {
    muted: 'border-border bg-card text-muted-foreground',
    warning: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    danger: 'border-destructive/40 bg-destructive/10 text-destructive',
};

export function SectionState({
    title,
    text,
    tone = 'muted',
    onRetry,
}: {
    title: string;
    text?: string;
    tone?: Tone;
    onRetry?: () => void;
}) {
    const Icon = tone === 'danger' ? ShieldAlert : tone === 'warning' ? AlertTriangle : Inbox;
    return (
        <div className={`rounded-lg border px-3 py-3 ${TONE_CLASS[tone]}`}>
            <div className="flex items-start gap-2">
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{title}</div>
                    {text && <div className="mt-0.5 whitespace-pre-wrap break-words text-xs">{text}</div>}
                </div>
                {onRetry && (
                    <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-[11px]" onClick={onRetry}>
                        <RefreshCw className="mr-1 h-3 w-3" />
                        Повторить
                    </Button>
                )}
            </div>
        </div>
    );
}

export function CardState({
    title,
    text,
    onRetry,
    icon,
    tone = 'muted',
}: {
    title: string;
    text?: string;
    onRetry?: () => void;
    icon?: ReactNode;
    tone?: Tone;
}) {
    return (
        <div className="flex h-full items-center justify-center p-6">
            <div className={`max-w-xl space-y-3 rounded-xl border px-5 py-5 text-center ${TONE_CLASS[tone]}`}>
                {icon && <div className="flex justify-center">{icon}</div>}
                <div className="text-sm font-medium text-foreground">{title}</div>
                {text && <div className="whitespace-pre-wrap break-words text-xs">{text}</div>}
                {onRetry && (
                    <Button size="sm" variant="outline" onClick={onRetry}>
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                        Повторить
                    </Button>
                )}
            </div>
        </div>
    );
}

export function InlineNotice({
    text,
    tone = 'muted',
    onRetry,
}: {
    text: string;
    tone?: Tone;
    onRetry?: () => void;
}) {
    return (
        <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${TONE_CLASS[tone]}`}>
            <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{text}</span>
            {onRetry && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onRetry}>
                    <RefreshCw className="mr-1 h-3 w-3" />
                    Повторить
                </Button>
            )}
        </div>
    );
}
